import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  IconButton,
  Typography,
  Chip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EditIcon from "@mui/icons-material/Edit";
import { useForecast } from "../../context/forecastContext";
import { forecastDataService } from "../../utils/services/forecast-data.service";
import NumberField from "../number-field";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { wmsService } from "../../utils/services/wmsProvider";
import { providerService } from "../../utils/services/providerService";
import { fieldSx } from "../../pages/manage-catalog/constants";
import config from "../../config";
import { LOST_SALES_SAFE_PERIOD_OPTIONS } from "../../utils/lostSalesParams";

const defaultImage = "/product.png";

const parsePositiveIntegerInput = (value) => {
  if (value === "") return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return null;
  return parsed;
};

const getSimilarProductImage = (imagesRaw) => {
  try {
    const images =
      typeof imagesRaw === "string" ? JSON.parse(imagesRaw) : imagesRaw;
    if (!Array.isArray(images) || images.length === 0) return defaultImage;
    const feature = images.find((img) => img.feature_image);
    const chosen = feature || images[0];
    if (chosen?.url) return chosen.url;
    return chosen?.image
      ? `${config.baseURL}/fetch-file/${chosen.image}`
      : defaultImage;
  } catch {
    return defaultImage;
  }
};

const ForecastParamsModal = () => {
  const {
    forecastDialogOpen,
    setForecastDialogOpen,
    forecastItems,
    horizon,
    setHorizon,
    horizonType,
    setHorizonType,
    selectedWarehouse,
    setSelectedWarehouse,
    selectedChannel,
    setSelectedChannel,
    forecastBy,
    setForecastBy,
    forecastByLocked,
    discountMax,
    setDiscountMax,
    discountType,
    discountMode,
    setDiscountMode,
    distributedDiscounts,
    setDistributedDiscounts,
    discountSectionCollapsed,
    setDiscountSectionCollapsed,
    percentageRange,
    handlePercentageRangeChange,
    handleForecastSubmit,
    similarProductDialogOpen,
    setSimilarProductDialogOpen,
    similarProducts,
    similarProductSelections,
    setSimilarProductSelections,
    similarActiveTab,
    setSimilarActiveTab,
    similarProductLoading,
    handleSimilarProductForecast,
    bulkResponses,
    bulkModalOpen,
    setBulkModalOpen,
    setForecastData,
    forecastType,
    includeLostSales,
    setIncludeLostSales,
    searchWindowDays,
    setSearchWindowDays,
    demandWindowDays,
    setDemandWindowDays,
    dowAdjustmentEnabled,
    setDowAdjustmentEnabled,
    editingLostSales,
    setEditingLostSales,
    leadTimeDays,
    setLeadTimeDays,
    safetyStockPct,
    setSafetyStockPct,
  } = useForecast();

  const invalidSearchWindow =
    includeLostSales &&
    (!Number.isInteger(searchWindowDays) || searchWindowDays <= 0);
  const nearestSafePeriodActive = demandWindowDays != null;
  const invalidDemandWindow =
    includeLostSales &&
    nearestSafePeriodActive &&
    (!Number.isInteger(demandWindowDays) || demandWindowDays <= 0);
  const demandExceedsSearch =
    includeLostSales &&
    nearestSafePeriodActive &&
    demandWindowDays > searchWindowDays;
  const invalidLostSalesParams =
    invalidSearchWindow || invalidDemandWindow || demandExceedsSearch;

  const onClickBulkResult = async (item) => {
    try {
      const res = await forecastDataService.get(item.data.forecast_data_id);
      setForecastData(
        { ...res.data, forecast_data_id: res.data._id },
        res.data.sku,
      );
      setBulkModalOpen(false);
    } catch (error) {
      console.error("Error fetching forecast data:", error);
    }
  };

  return (
    <>
      {/* Forecast Parameters Dialog */}
      <Dialog
        open={forecastDialogOpen}
        onClose={() => setForecastDialogOpen(false)}
        PaperProps={{
          sx: { width: { xs: "95vw", sm: 650 }, borderRadius: 2 },
        }}
      >
        <DialogTitle>Forecast Parameters</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: 2,
          }}
        >
          <TextField
            select
            style={{ marginTop: "5px" }}
            fullWidth
            label="Time Bucket"
            value={horizonType}
            onChange={(e) => setHorizonType(e.target.value)}
            sx={fieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ pr: 0 }}>
                  <Tooltip title="The unit of time each forecast period represents">
                    <IconButton size="small" edge="end" sx={{ mr: 1 }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="Daily">Daily</MenuItem>
            <MenuItem value="Weekly">Weekly</MenuItem>
            <MenuItem value="Monthly">Monthly</MenuItem>
            {/* <MenuItem value="Quarterly">Quarterly</MenuItem>
            <MenuItem value="Yearly">Yearly</MenuItem> */}
          </TextField>

          <NumberField
            label="Number of Periods"
            style={{ backgroundColor: "var(--bg-paper)" }}
            value={horizon}
            onChange={(val) => setHorizon(val)}
            fullWidth
            placeholder="Enter order"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ pr: 0, mr: 1 }}>
                  <Tooltip title="How many periods to include in the forecast">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            select
            fullWidth
            label="Forecast By"
            sx={fieldSx}
            value={forecastBy}
            onChange={(e) => setForecastBy(e.target.value)}
            disabled={forecastByLocked}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ pr: 0 }}>
                  <Tooltip title="The level at which inventory is forecasted">
                    <IconButton size="small" edge="end" sx={{ mr: 1 }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="SKU">SKU</MenuItem>
            <MenuItem value="Product">Product</MenuItem>
            {/* <MenuItem value="Category">Category</MenuItem> */}
            <MenuItem value="Similar Product">Similar Product</MenuItem>
          </TextField>

          {/* Supply-planning overrides */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
              gap: 2,
            }}
          >
            <NumberField
              label="Lead Time (days)"
              style={{ backgroundColor: "var(--bg-paper)" }}
              value={leadTimeDays}
              onChange={(val) =>
                setLeadTimeDays(val === "" ? "" : Math.max(0, Number(val)))
              }
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              placeholder="Enter lead time in days"
            />
            <NumberField
              label="Safety Stock (% of on hand)"
              style={{ backgroundColor: "var(--bg-paper)" }}
              value={safetyStockPct}
              onChange={(val) =>
                setSafetyStockPct(val === "" ? "" : Math.max(0, Number(val)))
              }
              inputProps={{ min: 0, step: 1 }}
              fullWidth
              placeholder="Enter safety stock %"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ fontWeight: 600 }}>
                    %
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {((forecastType != "warehouse" &&
            new Set(forecastItems.map((item) => item.client._id)).size == 1) ||
            forecastType === "product") && (
            <AutocompleteWithPagination
              selectedClient={forecastItems[0].client}
              selectedItem={selectedWarehouse}
              onDataChange={(warehouse) => setSelectedWarehouse(warehouse)}
              customLabel="Warehouse"
              optionKey={(item) => item?.name || item?.code}
              apiEndPoint={(limit, skip, search, clientId) =>
                wmsService.fetch(clientId, limit, skip)
              }
              width="100%"
              inputSx={fieldSx}
            />
          )}
          {forecastType != "channel" && (
            <AutocompleteWithPagination
              selectedItem={selectedChannel}
              onDataChange={(channel) => setSelectedChannel(channel)}
              customLabel="Channel"
              optionKey="name"
              apiEndPoint={(limit, skip, search) =>
                providerService.fetch(limit, skip, search, "OMS")
              }
              width="100%"
              inputSx={fieldSx}
            />
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "var(--text-primary)",
                }}
              >
                Include Lost Sales
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeLostSales}
                    onChange={(event) => {
                      setIncludeLostSales(event.target.checked);
                      if (!event.target.checked) {
                        setEditingLostSales(false);
                      }
                    }}
                  />
                }
                label={includeLostSales ? "On" : "Off"}
                labelPlacement="start"
                sx={{ fontFamily: "Poppins", mr: 0 }}
              />
            </Box>
            {includeLostSales && !editingLostSales && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                  p: 1.5,
                  border: "1px solid #a8a9ad",
                  borderRadius: 1,
                  backgroundColor: "var(--bg-paper)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: "text.secondary",
                    }}
                  >
                    Lost Sales
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Edit lost sales parameters"
                    onClick={() => setEditingLostSales(true)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Typography variant="body2" sx={{ fontFamily: "Poppins" }}>
                  Search Window: {searchWindowDays} days
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "Poppins" }}>
                  Nearest Safe Period:{" "}
                  {demandWindowDays == null
                    ? "Inactive"
                    : `${demandWindowDays} days`}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "Poppins" }}>
                  DOW Adjustment: {dowAdjustmentEnabled ? "On" : "Off"}
                </Typography>
              </Box>
            )}
            {includeLostSales && editingLostSales && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  p: 1.5,
                  border: "1px solid #a8a9ad",
                  borderRadius: 1,
                  backgroundColor: "var(--bg-paper)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "Poppins",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: "text.secondary",
                    }}
                  >
                    Lost Sales
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setEditingLostSales(false)}
                    disabled={invalidLostSalesParams}
                  >
                    Done
                  </Button>
                </Box>
                <TextField
                  label="Search Window (days)"
                  type="number"
                  value={searchWindowDays}
                  onChange={(event) => {
                    const parsed = parsePositiveIntegerInput(
                      event.target.value,
                    );
                    if (parsed !== null) setSearchWindowDays(parsed);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 1, step: 1 }}
                  fullWidth
                  error={invalidSearchWindow}
                  helperText={
                    invalidSearchWindow
                      ? "Enter a positive whole number of days."
                      : ""
                  }
                  sx={fieldSx}
                />
                <FormControl
                  fullWidth
                  error={invalidDemandWindow || demandExceedsSearch}
                  sx={fieldSx}
                >
                  <InputLabel id="forecast-nearest-safe-period-label" shrink>
                    Nearest Safe Period
                  </InputLabel>
                  <Select
                    labelId="forecast-nearest-safe-period-label"
                    label="Nearest Safe Period"
                    value={demandWindowDays ?? ""}
                    displayEmpty
                    onChange={(event) => {
                      const { value } = event.target;
                      setDemandWindowDays(value === "" ? null : Number(value));
                    }}
                  >
                    <MenuItem value="">Inactive</MenuItem>
                    {LOST_SALES_SAFE_PERIOD_OPTIONS.map((days) => (
                      <MenuItem key={days} value={days}>
                        {days} days
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {invalidDemandWindow
                      ? "Choose Inactive or a positive whole number of days."
                      : demandExceedsSearch
                        ? "Must not exceed the search window."
                        : nearestSafePeriodActive
                          ? "Find a safe period of N days within the search window."
                          : "Use the search window and exclude stockout days."}
                  </FormHelperText>
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dowAdjustmentEnabled}
                      onChange={(event) =>
                        setDowAdjustmentEnabled(event.target.checked)
                      }
                    />
                  }
                  label="DOW Adjustment"
                  sx={{ fontFamily: "Poppins", ml: 0 }}
                />
              </Box>
            )}
          </Box>
          {/* Promo / Campaign Discount Section */}
          <Box sx={{ mb: 2 }}>
            <Box
              onClick={() =>
                setDiscountSectionCollapsed(!discountSectionCollapsed)
              }
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                backgroundColor: "var(--bg-paper)",
                p: 1.5,
                border: "1px solid #a8a9ad",
                borderRadius: 1,
                transition: "background-color 0.2s ease",
                "&:hover": { backgroundColor: "var(--bg-hover)" },
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "var(--text-primary)",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                }}
              >
                Promo / Campaign Discount
              </Typography>
              {discountSectionCollapsed ? (
                <ExpandMoreIcon
                  sx={{ mr: 1, color: "var(--text-secondary)" }}
                />
              ) : (
                <ExpandLessIcon
                  sx={{ mr: 1, color: "var(--text-secondary)" }}
                />
              )}
            </Box>

            {!discountSectionCollapsed && (
              <Box
                sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}
              >
                {/* Discount Mode */}
                {forecastItems.length > 1 ? (
                  <Box sx={{ mb: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#374151",
                        mb: 1,
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Apply Discount As
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Button
                        variant={
                          discountMode === "flat" ? "contained" : "outlined"
                        }
                        onClick={() => setDiscountMode("flat")}
                        sx={{
                          flex: 1,
                          fontWeight: 600,
                          fontSize: "12px",
                          py: 1.2,
                          border: "1.5px solid",
                          transition: "all 0.2s ease",
                        }}
                      >
                        Uniform Discount
                      </Button>
                      <Button
                        variant={
                          discountMode === "distributed"
                            ? "contained"
                            : "outlined"
                        }
                        onClick={() => setDiscountMode("distributed")}
                        sx={{
                          flex: 1,
                          fontWeight: 600,
                          fontSize: "12px",
                          py: 1.2,
                          border: "1.5px solid",
                          transition: "all 0.2s ease",
                        }}
                      >
                        Per-SKU Discount
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <></>
                )}

                {/* Percentage Range Select */}
                {discountType === "%" && discountMode === "flat" ? (
                  <Box sx={{ mb: 0 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="percentage-range-label">
                        Select Discount
                      </InputLabel>
                      <Select
                        style={{ backgroundColor: "var(--bg-paper)" }}
                        labelId="percentage-range-label"
                        label="Select Discount"
                        value={percentageRange}
                        onChange={(e) =>
                          handlePercentageRangeChange(e.target.value)
                        }
                      >
                        {Array.from({ length: 10 }, (_, i) => (
                          <MenuItem key={i} value={`${(i + 1) * 5}`}>
                            {(i + 1) * 5}%
                          </MenuItem>
                        ))}
                        <MenuItem value="custom">Custom</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                ) : null}

                {/* Distributed Discount Inputs */}
                {discountMode === "distributed" ? (
                  <Box sx={{ mb: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "var(--text-primary)",
                        mb: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Set Percentage for Each SKU
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {forecastItems.map((item) => (
                        <Box
                          key={item._id}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                            alignItems: "center",
                          }}
                        >
                          <Box
                            sx={{
                              fontWeight: 600,
                              fontSize: "13px",
                              backgroundColor: "var(--bg-primary)",
                              p: 1.5,
                              borderRadius: 1,
                              textAlign: "center",
                              color: "var(--text-primary)",
                            }}
                          >
                            {item.sku}
                          </Box>
                          <Box sx={{ display: "grid" }}>
                            <TextField
                              type="number"
                              size="small"
                              value={distributedDiscounts[item._id]?.max || ""}
                              placeholder="0"
                              onChange={(e) => {
                                const val = Math.max(
                                  0,
                                  Number(e.target.value || 0),
                                );
                                setDistributedDiscounts((prev) => {
                                  const prevItem = prev[item._id] || {};
                                  const minVal = prevItem.min || 0;
                                  return {
                                    ...prev,
                                    [item._id]: {
                                      ...prevItem,
                                      max: val < minVal ? minVal : val,
                                    },
                                  };
                                });
                              }}
                              inputProps={{
                                min: distributedDiscounts[item._id]?.min || 0,
                              }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment
                                    position="end"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {discountType === "%" ? "%" : "$"}
                                  </InputAdornment>
                                ),
                              }}
                              sx={fieldSx}
                            />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <></>
                )}

                {/* Custom percentage input */}
                {percentageRange == "custom" ? (
                  <Box sx={{ mb: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#374151",
                        mb: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Discount Percentage
                    </Typography>
                    <Box sx={{ display: "grid" }}>
                      <NumberField
                        value={discountMax}
                        onChange={(val) =>
                          setDiscountMax(
                            val === "" ? "" : Math.max(0, Number(val)),
                          )
                        }
                        inputProps={{ min: 0 }}
                        size="small"
                        placeholder="Enter discount"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              sx={{ fontWeight: 600 }}
                            >
                              {discountType === "%" ? "%" : "$"}
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <></>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setForecastDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleForecastSubmit}
            disabled={invalidLostSalesParams}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Similar Product Selection Dialog */}
      <Dialog
        open={similarProductDialogOpen}
        onClose={() =>
          !similarProductLoading && setSimilarProductDialogOpen(false)
        }
        PaperProps={{
          sx: {
            width: { xs: "95vw", sm: 760 },
            borderRadius: 2,
            maxHeight: "88vh",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Select Similar Products</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          {similarProductLoading ? (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 0.5,
                  mb: 0.5,
                }}
              >
                <CircularProgress size={16} thickness={5} />
                <Typography
                  fontSize="13px"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ letterSpacing: "0.2px" }}
                >
                  Finding similar products…
                </Typography>
              </Box>
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1.5,
                    border: "1.5px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                    position: "relative",
                    opacity: 1 - i * 0.18,
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      inset: 0,
                      background:
                        "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.4s infinite",
                    },
                    "@keyframes shimmer": {
                      "0%": { backgroundPosition: "-200% 0" },
                      "100%": { backgroundPosition: "200% 0" },
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 1.5,
                      flexShrink: 0,
                      bgcolor: "action.hover",
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        height: 13,
                        width: `${68 - i * 6}%`,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                      }}
                    />
                    <Box
                      sx={{
                        height: 11,
                        width: `${45 - i * 4}%`,
                        borderRadius: 1,
                        bgcolor: "action.hover",
                      }}
                    />
                    <Box
                      sx={{
                        height: 10,
                        width: "25%",
                        borderRadius: 1,
                        bgcolor: "action.hover",
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      width: 88,
                      height: 24,
                      borderRadius: 10,
                      flexShrink: 0,
                      bgcolor: "action.hover",
                    }}
                  />
                </Box>
              ))}
            </Box>
          ) : (
            <>
              {forecastItems.length > 1 && (
                <Tabs
                  value={similarActiveTab}
                  onChange={(_, v) => setSimilarActiveTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    mb: 1,
                  }}
                >
                  {forecastItems.map((inv) => (
                    <Tab
                      key={inv._id}
                      label={inv.sku}
                      iconPosition="end"
                      icon={
                        similarProductSelections[inv._id] ? (
                          <Chip
                            size="small"
                            color="success"
                            label="✓"
                            sx={{ height: 18, fontSize: "10px" }}
                          />
                        ) : undefined
                      }
                    />
                  ))}
                </Tabs>
              )}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                {forecastItems.length === 1 ? (
                  <>
                    Select a similar product for{" "}
                    <strong>{forecastItems[0]?.sku}</strong>
                  </>
                ) : (
                  <>
                    Selecting for:{" "}
                    <strong>{forecastItems[similarActiveTab]?.sku}</strong>
                  </>
                )}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  overflowY: "auto",
                  maxHeight: 460,
                  pr: 0.5,
                }}
              >
                {(() => {
                  const currentInv =
                    forecastItems[similarActiveTab] || forecastItems[0];
                  const currentProducts =
                    similarProducts[currentInv?.product._id] || [];
                  if (currentProducts.length === 0) {
                    return (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        No similar products found.
                      </Typography>
                    );
                  }
                  return currentProducts.map((product) => {
                    const isSelected =
                      similarProductSelections[currentInv?._id] === product._id;
                    const score = (product._search_score * 100).toFixed(2);
                    const imgSrc = getSimilarProductImage(product.images);
                    return (
                      <Box
                        key={product._id}
                        onClick={() =>
                          setSimilarProductSelections((prev) => ({
                            ...prev,
                            [currentInv._id]: product._id,
                          }))
                        }
                        sx={{
                          p: 1.5,
                          border: "1.5px solid",
                          borderColor: isSelected ? "primary.main" : "divider",
                          borderRadius: 2,
                          cursor: "pointer",
                          backgroundColor: isSelected
                            ? "action.selected"
                            : "background.paper",
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: "primary.main",
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        <Box sx={{ position: "relative", flexShrink: 0 }}>
                          <Box
                            component="img"
                            src={imgSrc}
                            alt={product.name}
                            onError={(e) => {
                              e.target.src = defaultImage;
                            }}
                            sx={{
                              width: 72,
                              height: 72,
                              borderRadius: 2,
                              objectFit: "cover",
                              display: "block",
                              border: "1.5px solid",
                              borderColor: isSelected
                                ? "primary.main"
                                : "divider",
                              bgcolor: "action.hover",
                              transition: "border-color 0.2s ease",
                            }}
                          />
                          {isSelected && (
                            <CheckCircleIcon
                              sx={{
                                position: "absolute",
                                top: -6,
                                right: -6,
                                fontSize: 20,
                                color: "primary.main",
                                bgcolor: "background.paper",
                                borderRadius: "50%",
                              }}
                            />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                              mb: 0.4,
                            }}
                          >
                            <Typography
                              fontWeight={700}
                              fontSize="13px"
                              noWrap
                              color="text.primary"
                              sx={{ flex: "0 1 auto", maxWidth: "55%" }}
                            >
                              {product.name}
                            </Typography>
                            <Chip
                              label={`${score}%`}
                              size="small"
                              color={isSelected ? "primary" : "default"}
                              sx={{
                                fontWeight: 700,
                                fontSize: "10px",
                                height: 18,
                                "& .MuiChip-label": { px: 0.8 },
                              }}
                            />
                            {product.category && (
                              <Chip
                                label={product.category}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: "10px",
                                  height: 18,
                                  borderRadius: 1,
                                  "& .MuiChip-label": { px: 0.8 },
                                }}
                              />
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.8,
                            }}
                          >
                            <Typography fontSize="11px" color="text.secondary">
                              {product.no_of_sku} SKU
                              {product.no_of_sku !== 1 ? "s" : ""}
                            </Typography>
                            {product.product_type && (
                              <>
                                <Typography
                                  fontSize="11px"
                                  color="text.disabled"
                                >
                                  ·
                                </Typography>
                                <Typography
                                  fontSize="11px"
                                  color="text.secondary"
                                >
                                  {product.product_type}
                                </Typography>
                              </>
                            )}
                          </Box>
                          <Box
                            sx={{
                              mt: 1,
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1.5,
                              overflow: "hidden",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Box
                              sx={{
                                px: 1.2,
                                py: 0.5,
                                bgcolor: "action.hover",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.6,
                              }}
                            >
                              <Typography
                                fontSize="10px"
                                fontWeight={700}
                                color="text.secondary"
                                sx={{
                                  textTransform: "uppercase",
                                  letterSpacing: "0.4px",
                                }}
                              >
                                Monthly Orders
                              </Typography>
                              {Array.isArray(product.orders) &&
                                product.orders.length > 0 && (
                                  <Chip
                                    label={product.orders.length}
                                    size="small"
                                    sx={{
                                      height: 14,
                                      fontSize: "9px",
                                      "& .MuiChip-label": { px: 0.7 },
                                    }}
                                  />
                                )}
                            </Box>
                            {Array.isArray(product.orders) &&
                            product.orders.length > 0 ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.6,
                                  overflowX: "auto",
                                  p: 0.8,
                                  scrollbarWidth: "none",
                                  "&::-webkit-scrollbar": { display: "none" },
                                }}
                              >
                                {product.orders.map((o, oi) => {
                                  const label = new Date(
                                    o.order_month,
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    year: "2-digit",
                                  });
                                  return (
                                    <Box
                                      key={oi}
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        flexShrink: 0,
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        bgcolor: "background.paper",
                                        minWidth: 46,
                                      }}
                                    >
                                      <Typography
                                        fontSize="11px"
                                        fontWeight={700}
                                        color="text.primary"
                                        lineHeight={1.3}
                                      >
                                        {o.total_orders}
                                      </Typography>
                                      <Typography
                                        fontSize="9px"
                                        color="text.secondary"
                                        lineHeight={1.2}
                                        noWrap
                                      >
                                        {label}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            ) : (
                              <Box sx={{ px: 1.2, py: 0.8 }}>
                                <Typography
                                  fontSize="10px"
                                  color="text.disabled"
                                  fontStyle="italic"
                                >
                                  No orders found
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  });
                })()}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSimilarProductDialogOpen(false)}
            disabled={similarProductLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={
              similarProductLoading ||
              forecastItems.some((inv) => !similarProductSelections[inv._id])
            }
            onClick={handleSimilarProductForecast}
          >
            Forecast
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Forecast Results Dialog */}
      <Dialog
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        PaperProps={{ sx: { width: { xs: "90vw", sm: 500 }, borderRadius: 2 } }}
      >
        <DialogTitle>Forecast Results</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
        >
          {bulkResponses && bulkResponses.length > 0 ? (
            bulkResponses.length === 1 && bulkResponses[0].error ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  py: 4,
                  px: 2,
                }}
              >
                <svg
                  width="90"
                  height="90"
                  viewBox="0 0 90 90"
                  fill="none"
                  style={{ marginBottom: 20 }}
                >
                  <circle cx="45" cy="45" r="45" fill="#FEF2F2" />
                  <circle cx="45" cy="45" r="32" fill="#FEE2E2" />
                  <path
                    d="M45 24L64 57H26L45 24Z"
                    stroke="#DC2626"
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <line
                    x1="45"
                    y1="35"
                    x2="45"
                    y2="47"
                    stroke="#DC2626"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <circle cx="45" cy="53" r="2" fill="#DC2626" />
                </svg>
                <Typography
                  fontSize="16px"
                  fontWeight={600}
                  fontFamily="Poppins"
                  color="var(--text-primary)"
                  sx={{ mb: 1 }}
                >
                  Forecast Failed
                </Typography>
                {bulkResponses[0].sku && (
                  <Chip
                    label={`SKU: ${bulkResponses[0].sku}`}
                    size="small"
                    sx={{
                      mb: 1.5,
                      backgroundColor: "#FEE2E2",
                      color: "#991B1B",
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      fontWeight: 600,
                      borderRadius: "8px",
                    }}
                  />
                )}
                <Typography
                  fontSize="13px"
                  color="var(--text-secondary)"
                  fontFamily="Poppins"
                  sx={{ lineHeight: 1.6, maxWidth: 340 }}
                >
                  {bulkResponses[0].message || "Forecast failed for this SKU"}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
                {(() => {
                  const successCount = bulkResponses.filter(
                    (x) => !x.error,
                  ).length;
                  const errorCount = bulkResponses.filter(
                    (x) => x.error,
                  ).length;
                  return successCount > 0 && errorCount > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        mb: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                        label={`${successCount} succeeded`}
                        size="small"
                        sx={{
                          backgroundColor: "#DCFCE7",
                          color: "#166534",
                          fontFamily: "Poppins",
                          fontSize: "11px",
                          "& .MuiChip-icon": { color: "#16A34A" },
                        }}
                      />
                      <Chip
                        icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
                        label={`${errorCount} failed`}
                        size="small"
                        sx={{
                          backgroundColor: "#FEE2E2",
                          color: "#991B1B",
                          fontFamily: "Poppins",
                          fontSize: "11px",
                          "& .MuiChip-icon": { color: "#DC2626" },
                        }}
                      />
                    </Box>
                  ) : null;
                })()}
                {bulkResponses.map((item, index) =>
                  item.error ? (
                    <Box
                      key={"error-" + (item.sku || index)}
                      sx={{
                        p: 2,
                        mb: 2,
                        border: "1px solid #FCA5A5",
                        borderRadius: "12px",
                        backgroundColor: "#FEF2F2",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        mr: 2,
                      }}
                    >
                      <ErrorOutlineIcon
                        sx={{
                          color: "#DC2626",
                          flexShrink: 0,
                          mt: "2px",
                          fontSize: 18,
                        }}
                      />
                      <Box>
                        {item.sku && (
                          <Typography
                            fontSize="12px"
                            color="#991B1B"
                            fontWeight={600}
                            fontFamily="Poppins"
                            sx={{ mb: 0.3 }}
                          >
                            SKU: {item.sku}
                          </Typography>
                        )}
                        <Typography
                          fontSize="12px"
                          color="#DC2626"
                          fontWeight={400}
                          fontFamily="Poppins"
                          sx={{ lineHeight: 1.4 }}
                        >
                          {item.message || "Forecast failed for this SKU"}
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      key={item._id + " -- " + index}
                      sx={{
                        p: 2.5,
                        mb: 2,
                        border: "1px solid #E5E7EB",
                        borderRadius: "12px",
                        backgroundColor: "var(--bg-paper)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 2,
                        mr: 2,
                        cursor: "pointer",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                          borderColor: "#D1D5DB",
                          backgroundColor: "var(--bg-hover)",
                        },
                      }}
                      onClick={() => onClickBulkResult(item)}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          fontSize="13px"
                          color="var(--text-primary)"
                          fontWeight={
                            item.notification_status === "Read" ? 400 : 500
                          }
                          fontFamily="Poppins"
                          sx={{ mb: 0.6, lineHeight: 1.4 }}
                        >
                          {item.message || "No message"}
                        </Typography>
                        <Typography
                          fontSize="11px"
                          color="var(--text-secondary)"
                          fontWeight={400}
                          fontFamily="Poppins"
                        >
                          {new Date(item.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  ),
                )}
              </Box>
            )
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              <img
                src="no_notification.svg"
                alt="No notifications"
                style={{ width: 100, marginBottom: 16, opacity: 0.7 }}
              />
              <Typography fontSize={14} color="#6B7280">
                No notifications yet
              </Typography>
              <Typography fontSize={12} color="#9CA3AF" sx={{ mt: 0.5 }}>
                You're all caught up!
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ForecastParamsModal;
